/**
 * Plans & Pricing Page
 *
 * Enterprise features:
 * - Dynamic plan management from API
 * - Plan distribution stats
 * - Create/edit plan modals
 * - Billing period toggle
 * - Real-time subscription counts
 */

import { useState, useMemo, useCallback } from 'react';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import Modal from '../../components/modals/Modal';
import { plansApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useQuery, useMutation } from '../../hooks/useDataFetch';
import './Plans.css';

// Default plan template
const DEFAULT_NEW_PLAN = {
  name: '',
  price: 0,
  period: 'month',
  description: '',
  features: [],
  mauLimit: 1000,
  popular: false,
};

const Plans = () => {
  const { showToast } = useToast();
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [newPlan, setNewPlan] = useState(DEFAULT_NEW_PLAN);
  const [newFeature, setNewFeature] = useState('');

  // Fetch plans
  const {
    data: plansData,
    isLoading: loadingPlans,
    refetch: refetchPlans,
  } = useQuery(
    () => plansApi.list(),
    {
      key: 'plans:list',
      cacheTTL: 30000,
    }
  );

  // Fetch plan distribution
  const {
    data: distributionData,
    isLoading: loadingDistribution,
  } = useQuery(
    () => plansApi.distribution(),
    {
      key: 'plans:distribution',
      cacheTTL: 60000,
    }
  );

  // Create plan mutation
  const createPlanMutation = useMutation(
    (data) => plansApi.create(data),
    {
      onSuccess: () => {
        setShowCreateModal(false);
        setNewPlan(DEFAULT_NEW_PLAN);
        refetchPlans();
        showToast('Plan created successfully', 'success');
      },
      onError: (error) => {
        showToast(error.message || 'Failed to create plan', 'error');
      },
    }
  );

  // Update plan mutation
  const updatePlanMutation = useMutation(
    ({ id, data }) => plansApi.update(id, data),
    {
      onSuccess: () => {
        setShowEditModal(false);
        setSelectedPlan(null);
        refetchPlans();
        showToast('Plan updated successfully', 'success');
      },
      onError: (error) => {
        showToast(error.message || 'Failed to update plan', 'error');
      },
    }
  );

  // Delete plan mutation
  const deletePlanMutation = useMutation(
    (id) => plansApi.delete(id),
    {
      onSuccess: () => {
        refetchPlans();
        showToast('Plan deleted', 'success');
      },
      onError: (error) => {
        showToast(error.message || 'Failed to delete plan', 'error');
      },
    }
  );

  // Extract plans
  const plans = useMemo(() => {
    const data = plansData || {};
    return data.plans || data.data || data.items || [];
  }, [plansData]);

  // Extract distribution
  const distribution = useMemo(() => {
    const data = distributionData || {};
    return data.distribution || data.stats || data.data || [];
  }, [distributionData]);

  // Handlers
  const handleCreatePlan = useCallback(() => {
    if (!newPlan.name.trim()) {
      showToast('Please enter a plan name', 'error');
      return;
    }
    createPlanMutation.mutate(newPlan);
  }, [newPlan, createPlanMutation, showToast]);

  const handleUpdatePlan = useCallback(() => {
    if (!selectedPlan) return;
    updatePlanMutation.mutate({ id: selectedPlan.id, data: selectedPlan });
  }, [selectedPlan, updatePlanMutation]);

  const handleEditPlan = useCallback((plan) => {
    setSelectedPlan({ ...plan });
    setShowEditModal(true);
  }, []);

  const handleDeletePlan = useCallback((plan) => {
    if (window.confirm(`Are you sure you want to delete the "${plan.name}" plan?`)) {
      deletePlanMutation.mutate(plan.id);
    }
  }, [deletePlanMutation]);

  const addFeature = useCallback((isEdit = false) => {
    if (!newFeature.trim()) return;

    if (isEdit && selectedPlan) {
      setSelectedPlan(prev => ({
        ...prev,
        features: [...(prev.features || []), newFeature.trim()],
      }));
    } else {
      setNewPlan(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()],
      }));
    }
    setNewFeature('');
  }, [newFeature, selectedPlan]);

  const removeFeature = useCallback((index, isEdit = false) => {
    if (isEdit && selectedPlan) {
      setSelectedPlan(prev => ({
        ...prev,
        features: prev.features.filter((_, i) => i !== index),
      }));
    } else {
      setNewPlan(prev => ({
        ...prev,
        features: prev.features.filter((_, i) => i !== index),
      }));
    }
  }, [selectedPlan]);

  // Helpers
  function getPrice(plan) {
    const price = plan.price || 0;
    if (billingPeriod === 'yearly') {
      return Math.round(price * 0.8);
    }
    return price;
  }

  function getDistributionIcon(planName) {
    const icons = {
      free: 'user',
      starter: 'users',
      pro: 'zap',
      enterprise: 'building',
    };
    return icons[planName?.toLowerCase()] || 'package';
  }

  function getDistributionClass(planName) {
    const classes = {
      free: 'free',
      starter: 'starter',
      pro: 'pro',
      enterprise: 'enterprise',
    };
    return classes[planName?.toLowerCase()] || 'default';
  }

  return (
    <div className="plans-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Plans & Pricing</h1>
          <p className="page-subtitle">Manage subscription plans and pricing tiers</p>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            icon="refresh"
            onClick={refetchPlans}
            disabled={loadingPlans}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            icon="plus"
            onClick={() => setShowCreateModal(true)}
          >
            Create Plan
          </Button>
        </div>
      </div>

      {/* Billing Toggle */}
      <div className="billing-toggle">
        <button
          className={`toggle-btn ${billingPeriod === 'monthly' ? 'active' : ''}`}
          onClick={() => setBillingPeriod('monthly')}
        >
          Monthly
        </button>
        <button
          className={`toggle-btn ${billingPeriod === 'yearly' ? 'active' : ''}`}
          onClick={() => setBillingPeriod('yearly')}
        >
          Yearly
          <span className="save-badge">Save 20%</span>
        </button>
      </div>

      {/* Plans Grid */}
      <div className="plans-grid">
        {loadingPlans && plans.length === 0 ? (
          // Skeleton loading
          [1, 2, 3, 4].map((i) => (
            <div key={i} className="plan-card skeleton">
              <div className="skeleton-header">
                <div className="skeleton-line" style={{ width: '80px' }}></div>
                <div className="skeleton-line short" style={{ width: '120px' }}></div>
              </div>
              <div className="skeleton-price"></div>
              <div className="skeleton-features">
                <div className="skeleton-line"></div>
                <div className="skeleton-line"></div>
                <div className="skeleton-line"></div>
              </div>
            </div>
          ))
        ) : plans.length > 0 ? (
          plans.map((plan) => (
            <div
              key={plan.id}
              className={`plan-card ${plan.popular ? 'popular' : ''} ${plan.current ? 'current' : ''}`}
            >
              {plan.popular && <div className="popular-badge">Most Popular</div>}
              {plan.current && <div className="current-badge">Current Plan</div>}

              <div className="plan-header">
                <h3 className="plan-name">{plan.name}</h3>
                <p className="plan-description">{plan.description}</p>
              </div>

              <div className="plan-price">
                <span className="price-amount">
                  ${getPrice(plan)}
                </span>
                <span className="price-period">
                  {plan.price === 0 ? '' : `/${billingPeriod === 'yearly' ? 'month, billed yearly' : 'month'}`}
                </span>
              </div>

              <ul className="plan-features">
                {(plan.features || []).map((feature, index) => (
                  <li key={index}>
                    <Icon name="check" size="sm" />
                    {feature}
                  </li>
                ))}
              </ul>

              <div className="plan-actions">
                <Button
                  variant="ghost"
                  size="sm"
                  icon="edit"
                  onClick={() => handleEditPlan(plan)}
                >
                  Edit
                </Button>
                {!plan.current && plan.price > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="trash"
                    onClick={() => handleDeletePlan(plan)}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="plans-empty">
            <Icon name="package" size="xl" />
            <p>No plans configured</p>
            <Button
              variant="primary"
              icon="plus"
              onClick={() => setShowCreateModal(true)}
            >
              Create First Plan
            </Button>
          </div>
        )}
      </div>

      {/* Plan Distribution */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">Plan Distribution</h3>
        </div>

        <div className="plan-stats">
          {loadingDistribution && distribution.length === 0 ? (
            [1, 2, 3, 4].map((i) => (
              <div key={i} className="plan-stat-card skeleton">
                <div className="skeleton-icon"></div>
                <div className="skeleton-content">
                  <div className="skeleton-line"></div>
                  <div className="skeleton-line short"></div>
                </div>
              </div>
            ))
          ) : distribution.length > 0 ? (
            distribution.map((item, index) => (
              <div key={index} className="plan-stat-card">
                <div className={`plan-stat-icon ${getDistributionClass(item.plan || item.name)}`}>
                  <Icon name={getDistributionIcon(item.plan || item.name)} />
                </div>
                <div className="plan-stat-info">
                  <div className="plan-stat-value">{item.count || item.tenantCount || 0}</div>
                  <div className="plan-stat-label">{item.plan || item.name}</div>
                </div>
              </div>
            ))
          ) : plans.length > 0 ? (
            // If no distribution but we have plans, show based on plans
            plans.map((plan, index) => (
              <div key={index} className="plan-stat-card">
                <div className={`plan-stat-icon ${getDistributionClass(plan.name)}`}>
                  <Icon name={getDistributionIcon(plan.name)} />
                </div>
                <div className="plan-stat-info">
                  <div className="plan-stat-value">{plan.subscriberCount || 0}</div>
                  <div className="plan-stat-label">{plan.name}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="distribution-empty">
              <p>No distribution data available</p>
            </div>
          )}
        </div>
      </section>

      {/* Create Plan Modal */}
      {showCreateModal && (
        <Modal
          title="Create Plan"
          onClose={() => {
            setShowCreateModal(false);
            setNewPlan(DEFAULT_NEW_PLAN);
            setNewFeature('');
          }}
        >
          <div className="plan-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="planName">Plan Name</label>
                <input
                  id="planName"
                  type="text"
                  placeholder="e.g., Pro"
                  value={newPlan.name}
                  onChange={(e) => setNewPlan(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="planPrice">Price ($/month)</label>
                <input
                  id="planPrice"
                  type="number"
                  min="0"
                  placeholder="99"
                  value={newPlan.price}
                  onChange={(e) => setNewPlan(prev => ({ ...prev, price: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="planDescription">Description</label>
              <input
                id="planDescription"
                type="text"
                placeholder="For growing businesses"
                value={newPlan.description}
                onChange={(e) => setNewPlan(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label htmlFor="mauLimit">MAU Limit</label>
              <input
                id="mauLimit"
                type="number"
                min="0"
                placeholder="10000"
                value={newPlan.mauLimit}
                onChange={(e) => setNewPlan(prev => ({ ...prev, mauLimit: parseInt(e.target.value) || 0 }))}
              />
            </div>

            <div className="form-group">
              <label>Features</label>
              <div className="features-input">
                <input
                  type="text"
                  placeholder="Add a feature"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  icon="plus"
                  onClick={() => addFeature()}
                >
                  Add
                </Button>
              </div>
              <div className="features-list">
                {newPlan.features.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <span>{feature}</span>
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      aria-label="Remove feature"
                    >
                      <Icon name="x" size="xs" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={newPlan.popular}
                  onChange={(e) => setNewPlan(prev => ({ ...prev, popular: e.target.checked }))}
                />
                Mark as "Most Popular"
              </label>
            </div>

            <div className="modal-actions">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCreateModal(false);
                  setNewPlan(DEFAULT_NEW_PLAN);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreatePlan}
                disabled={createPlanMutation.isLoading || !newPlan.name.trim()}
              >
                {createPlanMutation.isLoading ? 'Creating...' : 'Create Plan'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Plan Modal */}
      {showEditModal && selectedPlan && (
        <Modal
          title={`Edit ${selectedPlan.name}`}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPlan(null);
            setNewFeature('');
          }}
        >
          <div className="plan-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="editPlanName">Plan Name</label>
                <input
                  id="editPlanName"
                  type="text"
                  value={selectedPlan.name}
                  onChange={(e) => setSelectedPlan(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="editPlanPrice">Price ($/month)</label>
                <input
                  id="editPlanPrice"
                  type="number"
                  min="0"
                  value={selectedPlan.price}
                  onChange={(e) => setSelectedPlan(prev => ({ ...prev, price: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="editPlanDescription">Description</label>
              <input
                id="editPlanDescription"
                type="text"
                value={selectedPlan.description || ''}
                onChange={(e) => setSelectedPlan(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label>Features</label>
              <div className="features-input">
                <input
                  type="text"
                  placeholder="Add a feature"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature(true))}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  icon="plus"
                  onClick={() => addFeature(true)}
                >
                  Add
                </Button>
              </div>
              <div className="features-list">
                {(selectedPlan.features || []).map((feature, index) => (
                  <div key={index} className="feature-item">
                    <span>{feature}</span>
                    <button
                      type="button"
                      onClick={() => removeFeature(index, true)}
                      aria-label="Remove feature"
                    >
                      <Icon name="x" size="xs" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={selectedPlan.popular || false}
                  onChange={(e) => setSelectedPlan(prev => ({ ...prev, popular: e.target.checked }))}
                />
                Mark as "Most Popular"
              </label>
            </div>

            <div className="modal-actions">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedPlan(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleUpdatePlan}
                disabled={updatePlanMutation.isLoading}
              >
                {updatePlanMutation.isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Plans;
